import QRCode from 'qrcode';

const BT_PRINTER_NAME_KEY = 'bt_printer_name';
const BT_PRINTER_DEVICE_KEY = 'bt_printer_device_id';

const ESC = 0x1B;
const GS = 0x1D;
const CMD = {
  INIT: [ESC, 0x40],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_HEIGHT: [ESC, 0x21, 0x10],
  NORMAL_SIZE: [ESC, 0x21, 0x00],
  CUT: [GS, 0x56, 0x41, 0x10],
  FEED: [ESC, 0x64, 0x04],
  REVERSE_ON: [GS, 0x42, 0x01],
  REVERSE_OFF: [GS, 0x42, 0x00],
};

function textToBytes(text: string): number[] {
  return Array.from(new TextEncoder().encode(text));
}
function divider(width = 32): number[] {
  return textToBytes('-'.repeat(width) + '\n');
}
function row(left: string, right: string, width = 32): number[] {
  // ₹ (and any other non-ASCII symbol) is multi-byte in UTF-8 — sending
  // it raw via textToBytes gets decoded byte-by-byte against the
  // printer's own codepage (garbled glyphs like "Γé┤"), and the extra
  // phantom bytes also throw off the padding math, wrapping the line.
  // Route through the same raster fallback textLineBytes() already uses
  // for non-ASCII text.
  if (!isAsciiOnly(left + right)) {
    return rasterizeRow(left, right);
  }
  const space = width - left.length - right.length;
  return textToBytes(left + ' '.repeat(Math.max(1, space)) + right + '\n');
}
function row3(a: string, b: string, c: string, widths: [number, number, number] = [16, 4, 12]): number[] {
  if (!isAsciiOnly(a + b + c)) {
    return rasterize3ColLine(a, b, c);
  }
  const [wa, wb, wc] = widths;
  const aStr = (a.length > wa ? a.slice(0, wa - 1) + ' ' : a.padEnd(wa));
  const bStr = b.padStart(wb);
  const cStr = c.padStart(wc);
  return textToBytes(aStr + bStr + cStr + '\n');
}

function isAsciiOnly(text: string): boolean {
  return /^[\x00-\x7F]*$/.test(text);
}

// Renders one line of text as a canvas bitmap and sends it as an ESC/POS raster
// image. Works for ANY script/language since it's pixels, not printer-decoded
// bytes — the printer never needs to understand the characters.
function rasterizeTextLine(text: string, opts: { width?: number; fontSize?: number; align?: 'left' | 'center'; bold?: boolean } = {}): number[] {
  const printerWidthDots = opts.width || 384; // 58mm at 203dpi
  const fontSize = opts.fontSize || 24;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const fontSpec = `${opts.bold ? 'bold ' : ''}${fontSize}px sans-serif`;
  const lineHeight = Math.ceil(fontSize * 1.5);
  canvas.width = printerWidthDots;
  canvas.height = lineHeight;
  ctx.font = fontSpec; // reapply — resizing canvas resets context state
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';
  const align = opts.align || 'left';
  ctx.textAlign = align === 'center' ? 'center' : 'left';
  const x = align === 'center' ? canvas.width / 2 : 6;
  ctx.fillText(text, x, canvas.height / 2, canvas.width - 12);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x2 = 0; x2 < canvas.width; x2++) {
      const i = (y * canvas.width + x2) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) {
        const byteIndex = y * widthBytes + Math.floor(x2 / 8);
        pixels[byteIndex] |= 1 << (7 - (x2 % 8));
      }
    }
  }

  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;

  return [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}

const ARABIC_INDIC_DIGITS: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
export function toArabicIndicDigits(text: string): string {
  return text.replace(/[0-9]/g, d => ARABIC_INDIC_DIGITS[d]);
}

function rasterizeRow(left: string, right: string, opts: { width?: number; fontSize?: number } = {}): number[] {
  const printerWidthDots = opts.width || 384;
  const fontSize = opts.fontSize || 22;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const lineHeight = Math.ceil(fontSize * 1.5);
  canvas.width = printerWidthDots;
  canvas.height = lineHeight;
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(left, 6, canvas.height / 2, canvas.width * 0.62);
  ctx.textAlign = 'right';
  ctx.fillText(right, canvas.width - 6, canvas.height / 2, canvas.width * 0.4);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        pixels[byteIndex] |= 1 << (7 - (x % 8));
      }
    }
  }
  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;
  return [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}


function rasterize3ColLine(a: string, b: string, c: string, opts: { width?: number; fontSize?: number } = {}): number[] {
  const printerWidthDots = opts.width || 384;
  const fontSize = opts.fontSize || 20;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const lineHeight = Math.ceil(fontSize * 1.5);
  canvas.width = printerWidthDots;
  canvas.height = lineHeight;
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';

  // Matches row3()'s 16:4:12 char-width ratio (out of 32 total)
  const col1W = printerWidthDots * 0.5;
  const col2W = printerWidthDots * 0.125;
  const col3W = printerWidthDots * 0.375;

  ctx.textAlign = 'left';
  ctx.fillText(a, 6, canvas.height / 2, col1W - 8);
  ctx.textAlign = 'right';
  ctx.fillText(b, col1W + col2W - 4, canvas.height / 2, col2W - 4);
  ctx.fillText(c, col1W + col2W + col3W - 6, canvas.height / 2, col3W - 8);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x2 = 0; x2 < canvas.width; x2++) {
      const i = (y * canvas.width + x2) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) {
        const byteIndex = y * widthBytes + Math.floor(x2 / 8);
        pixels[byteIndex] |= 1 << (7 - (x2 % 8));
      }
    }
  }
  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;
  return [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}
// N-column raster row with per-column alignment.
function rasterizeNColLine(cells: string[], fractions: number[], aligns: ('left' | 'center' | 'right')[] = [], opts: { width?: number; fontSize?: number; invert?: boolean } = {}): number[] {
  const printerWidthDots = opts.width || 384;
  const fontSize = opts.fontSize || 20;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = printerWidthDots;
  canvas.height = Math.ceil(fontSize * 1.5);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';
  if (opts.invert) {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
  }
  let left = 0;
  cells.forEach((text, i) => {
    const w = printerWidthDots * fractions[i];
    const a = aligns[i] || (i === 0 ? 'left' : 'right');
    ctx.textAlign = a;
    const x = a === 'left' ? left + 6 : a === 'center' ? left + w / 2 : left + w - 4;
    ctx.fillText(text, x, canvas.height / 2, w - 8);
    left += w;
  });
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) pixels[y * widthBytes + Math.floor(x / 8)] |= 1 << (7 - (x % 8));
    }
  }
  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;
  return [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}

export interface PrintColumn {
  header: string;
  headerAr?: string;
  align?: 'left' | 'center' | 'right';
  width?: number;
  primary?: boolean; // the wide "name" column
}

function primaryIndex(cols: PrintColumn[]): number {
  const i = cols.findIndex(c => c.primary);
  return i >= 0 ? i : 0;
}

function colFractions(cols: PrintColumn[]): number[] {
  const total = cols.reduce((s, c) => s + (c.width || 1), 0) || 1;
  let f = cols.map(c => (c.width || 1) / total);
  const p = primaryIndex(cols);
  if (f.length > 1 && f[p] < 0.34) {
    const rest = 1 - f[p];
    f = f.map((v, i) => (i === p ? 0.34 : (v / rest) * 0.66));
  }
  return f;
}

// One receipt row for any set of columns.
function rowN(cells: string[], cols: PrintColumn[]): number[] {
  const fr = colFractions(cols);
  const p = primaryIndex(cols);
  const aligns = cols.map((c, i) => c.align || (i === p ? 'left' : 'right'));
  if (!isAsciiOnly(cells.join(''))) return rasterizeNColLine(cells, fr, aligns);

  const W = 32;
  const widths = fr.map(f => Math.max(2, Math.floor(f * W)));
  widths[p] += W - widths.reduce((s, w) => s + w, 0);
  if (cells.some((t, i) => i !== p && t.length > widths[i])) {
    return rasterizeNColLine(cells, fr, aligns);
  }
  const line = cells.map((t, i) => {
    const w = widths[i];
    const s = t.length > w ? t.slice(0, Math.max(1, w - 1)) + ' ' : t;
    if (aligns[i] === 'right') return s.padStart(w);
    if (aligns[i] === 'center') {
      const l = Math.floor((w - s.length) / 2);
      return (' '.repeat(l) + s).padEnd(w);
    }
    return s.padEnd(w);
  }).join('');
  return textToBytes(line + '\n');
}

// Fallback used only when the invoice has no readable columns.
function row4(a: string, b: string, c: string, d: string): number[] {
  return rowN([a, b, c, d], [
    { header: '', primary: true, width: 36 },
    { header: '', width: 10 },
    { header: '', width: 26 },
    { header: '', width: 28 },
  ]);
}

// Fast text bytes for ASCII, raster fallback for any other script.
// arabicNumerals: when true, converts 0-9 to Arabic-Indic digits before
// rasterizing — only applies to non-ASCII (already-translated) lines, so
// plain English lines are never affected.
function textLineBytes(
  text: string,
  opts: { width?: number; fontSize?: number; align?: 'left' | 'center'; bold?: boolean } = {},
  arabicNumerals?: boolean
): number[] {
  if (!text) return [];
  if (isAsciiOnly(text)) return textToBytes(text + '\n');
  const finalText = arabicNumerals ? toArabicIndicDigits(text) : text;
  return rasterizeTextLine(finalText, opts);
}

function qrImage(data: string, moduleSize = 6): number[] {
  const qr = QRCode.create(data, {
    errorCorrectionLevel: 'M',
  });

  const modules = qr.modules.size;
  const matrix = qr.modules.data;

  // 4-module quiet zone around the QR
  const quietZone = 4;

  const qrModules = modules + quietZone * 2;
  const width = qrModules * moduleSize;

  // ESC/POS raster data must use whole bytes
  const widthBytes = Math.ceil(width / 8);
  const rasterWidth = widthBytes * 8;

  const height = width;

  const pixels = new Uint8Array(widthBytes * height);

  for (let y = 0; y < height; y++) {
    const qrY = Math.floor(y / moduleSize) - quietZone;

    for (let x = 0; x < width; x++) {
      const qrX = Math.floor(x / moduleSize) - quietZone;

      let black = false;

      if (
        qrX >= 0 &&
        qrX < modules &&
        qrY >= 0 &&
        qrY < modules
      ) {
        black = matrix[qrY * modules + qrX] === 1;
      }

      if (black) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        const bit = 7 - (x % 8);
        pixels[byteIndex] |= 1 << bit;
      }
    }
  }

  const xL = widthBytes & 0xff;
  const xH = (widthBytes >> 8) & 0xff;
  const yL = height & 0xff;
  const yH = (height >> 8) & 0xff;

  return [
    // Center image
    ...CMD.ALIGN_CENTER,

    // GS v 0 — print raster bit image
    GS,
    0x76,
    0x30,
    0x00,
    xL,
    xH,
    yL,
    yH,

    ...Array.from(pixels),

    ...CMD.ALIGN_CENTER,
  ];
}

export interface PrintJob {
  shopName?: string;
  invoiceNumber?: string;
  date?: string;
  clientName?: string;
  items: { description: string; qty: number; rate: number; arabicName?: string }[];
  subtotal?: number;
  total?: number;
  taxLines?: { label: string; rate: number; amount?: number; labelAr?: string }[];
  discountLines?: { label: string; rate: number; amount?: number; labelAr?: string }[];
  shippingCost?: number;
  sym?: string;
  notes?: string;
  footer?: string;
  invoiceQR?: string;
  trackingQR?: string;
  headerLines?: string[];
  footerLines?: string[];
  invoiceQRLabel?: string;
  trackingQRLabel?: string;
  arabicNumerals?: boolean;
  clientLineAr?: string;
  rowLabelsAr?: { subtotal?: string; total?: string; shipping?: string; item?: string; qty?: string; amt?: string; tax?: string };
  // Template Fields (Patient, Age, Power R/L/PD, etc.) — the USB/BT
  // receipt path builds its own text output from PrintJob and never had
  // access to these before, which is why they printed fine in the
  // preview/PDF (BilingualInvoiceRenderer) but were silently missing on
  // the physical receipt.
  dynamicFields?: { label: string; value: string; labelAr?: string; valueAr?: string }[];
  amountInWords?: string;
  amountInWordsAr?: string;
  productTaxTotal?: number;
  netSubtotal?: number;
  clientPhone?: string;
  clientEmail?: string;
  buyerTaxId?: string;
  buyerTaxLabel?: string;
  itemExtraLines?: string[];
  // Arabic version of itemExtraLines — same index alignment, built from
  // each overflow column's arabicHeader instead of its English header.
  // Without this, overflow columns (anything past the first 4 inline
  // columns, e.g. Tax) never got a bilingual line at all — only the
  // item description and the totals block had English+Arabic pairs.
  itemExtraLinesAr?: string[];
  tableColumns?: PrintColumn[];
  itemCells?: string[][];
  autoDiscountAmount?: number;
  autoDiscountLabel?: string;
  notesAr?: string;
  taxBreakdown?: { name: string; rate: number; amount: number; nameAr?: string }[];
}

interface PrintJobOldUnused {
  shopName?: string;
  invoiceNumber?: string;
  date?: string;
  clientName?: string;
  items: { description: string; qty: number; rate: number; arabicName?: string }[];
  taxLines?: { label: string; rate: number; labelAr?: string }[];
  discountLines?: { label: string; rate: number; labelAr?: string }[];
  shippingCost?: number;
  sym?: string;
  notes?: string;
  footer?: string;
  invoiceQR?: string;
  trackingQR?: string;
  headerLines?: string[];
  footerLines?: string[];
  invoiceQRLabel?: string;
  trackingQRLabel?: string;
  arabicNumerals?: boolean;
  clientLineAr?: string;
  rowLabelsAr?: { subtotal?: string; total?: string; shipping?: string; item?: string; qty?: string; amt?: string; tax?: string };
  // Template Fields (Patient, Age, Power R/L/PD, etc.) — the USB/BT
  // receipt path builds its own text output from PrintJob and never had
  // access to these before, which is why they printed fine in the
  // preview/PDF (BilingualInvoiceRenderer) but were silently missing on
  // the physical receipt.
  dynamicFields?: { label: string; value: string; labelAr?: string; valueAr?: string }[];
  // Named GST/VAT/QST component breakdown (CGST/SGST/IGST/QST/etc.) —
  // mirrors the `taxBreakdown` field already used by POSReceiptRenderer
  // and the PDF export paths. When present, printed in place of the
  // generic blended "Total Tax (X%)" line below. nameAr carries the
  // transliterated component name for bilingual receipts, same pattern
  // as taxLines/discountLines' labelAr.
  taxBreakdown?: { name: string; rate: number; amount: number; nameAr?: string }[];
}

function getDeviceIcon(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('printer') || n.includes('xprinter') || n.includes('epson') || n.includes('star') || n.includes('xp-')) return '🖨️';
  if (n.includes('iphone') || n.includes('android') || n.includes('galaxy') || n.includes('pixel') || n.includes('phone')) return '📱';
  if (n.includes('ipad') || n.includes('tablet')) return '📱';
  if (n.includes('macbook') || n.includes('laptop') || n.includes('pc') || n.includes('computer')) return '💻';
  if (n.includes('airpod') || n.includes('earbuds') || n.includes('headphone') || n.includes('jbl') || n.includes('sony') || n.includes('bose')) return '🎧';
  if (n.includes('watch')) return '⌚';
  if (n.includes('speaker')) return '🔊';
  return '📡';
}

function isPrinterDevice(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('printer') || n.includes('xprinter') || n.includes('epson') || n.includes('star') || n.includes('pos') || n.includes('xp-') || n.includes('thermal') || n.includes('receipt');
}

function injectStyles() {
  if (document.getElementById('bt-modal-styles')) return;
  const s = document.createElement('style');
  s.id = 'bt-modal-styles';
  s.textContent = `
    @keyframes btspin { to { transform: rotate(360deg); } }
    @keyframes btpulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.7)} }
    @keyframes btfadein { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    #bt-modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;font-family:system-ui,sans-serif; }
    #bt-modal { background:#fff;border-radius:16px;width:100%;max-width:400px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.3);animation:btfadein 0.2s ease; }
    #bt-modal-header { padding:18px 20px 14px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;justify-content:space-between; }
    #bt-modal-title { font-size:15px;font-weight:700;color:#0f172a; }
    #bt-modal-sub { font-size:12px;color:#64748b;margin-top:2px; }
    #bt-scan-bar { padding:10px 20px;background:#f8fafc;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;gap:10px;font-size:12px;color:#64748b; }
    #bt-scan-dot { width:8px;height:8px;border-radius:50%;background:#3b82f6;animation:btpulse 1.2s ease-in-out infinite;flex-shrink:0; }
    #bt-device-list { max-height:280px;overflow-y:auto; }
    .bt-device-item { display:flex;align-items:center;gap:12px;padding:12px 20px;cursor:pointer;border-bottom:1px solid #f8fafc;transition:background 0.1s; }
    .bt-device-item:hover { background:#f8fafc; }
    .bt-device-item.bt-selected { background:#eff6ff;border-left:3px solid #3b82f6; }
    .bt-device-item:last-child { border-bottom:none; }
    .bt-device-icon { width:38px;height:38px;border-radius:10px;background:#f1f5f9;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0; }
    .bt-device-info { flex:1;min-width:0; }
    .bt-device-name { font-size:13px;font-weight:600;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
    .bt-device-type { font-size:11px;color:#64748b;margin-top:1px; }
    .bt-device-right { display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0; }
    .bt-signal { display:flex;gap:2px;align-items:flex-end; }
    .bt-signal span { display:block;background:#e2e8f0;border-radius:1px;width:3px; }
    .bt-signal span.on { background:#22c55e; }
    .bt-badge { font-size:10px;padding:2px 7px;border-radius:20px;font-weight:600; }
    .bt-badge-printer { background:#eff6ff;color:#1d4ed8; }
    .bt-badge-default { background:#f0fdf4;color:#15803d; }
    #bt-modal-footer { padding:14px 20px;border-top:1px solid #f1f5f9;display:flex;gap:8px; }
    #bt-cancel-btn { flex:1;padding:9px 14px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:1px solid #e2e8f0;background:#f8fafc;color:#374151; }
    #bt-connect-btn { flex:2;padding:9px 14px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;border:none;background:#1d4ed8;color:#fff;opacity:0.4;pointer-events:none; }
    #bt-connect-btn.active { opacity:1;pointer-events:auto; }
    #bt-empty { padding:40px 20px;text-align:center;color:#94a3b8;font-size:13px; }
    #bt-toast { position:fixed;top:24px;left:50%;transform:translateX(-50%);background:#1e293b;color:#fff;padding:14px 22px;border-radius:14px;font-family:system-ui,sans-serif;z-index:100000;box-shadow:0 8px 32px rgba(0,0,0,0.35);font-size:13px;font-weight:600;min-width:280px;text-align:center; }
  `;
  document.head.appendChild(s);
}

function removeModal() {
  document.getElementById('bt-modal-overlay')?.remove();
}

function removeToast() {
  document.getElementById('bt-toast')?.remove();
}

function showToast(msg: string, color = '#1e293b', autoDismiss = 0) {
  removeToast();
  const t = document.createElement('div');
  t.id = 'bt-toast';
  t.style.background = color;
  t.textContent = msg;
  document.body.appendChild(t);
  if (autoDismiss) setTimeout(removeToast, autoDismiss);
}

function signalBars(rssi?: number): string {
  // RSSI: -50 strong, -70 medium, -85 weak, unknown = 2 bars
  const bars = !rssi ? 2 : rssi > -55 ? 4 : rssi > -68 ? 3 : rssi > -80 ? 2 : 1;
  return `<div class="bt-signal">
    <span class="${bars >= 1 ? 'on' : ''}" style="height:5px"></span>
    <span class="${bars >= 2 ? 'on' : ''}" style="height:8px"></span>
    <span class="${bars >= 3 ? 'on' : ''}" style="height:11px"></span>
    <span class="${bars >= 4 ? 'on' : ''}" style="height:14px"></span>
  </div>`;
}

// Show the custom device picker modal
// Returns the selected BluetoothDevice or null
function showDevicePicker(devices: BluetoothDevice[]): Promise<BluetoothDevice | null> {
  injectStyles();
  removeModal();

  const defaultName = getBTPrinterName();

  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.id = 'bt-modal-overlay';

    const sorted = [...devices].sort((a, b) => {
      const ap = isPrinterDevice(a.name || '');
      const bp = isPrinterDevice(b.name || '');
      return ap === bp ? 0 : ap ? -1 : 1;
    });

    let selectedDevice: BluetoothDevice | null =
      sorted.find(d => d.name === defaultName) || sorted.find(d => isPrinterDevice(d.name || '')) || null;

    function renderList() {
      const list = document.getElementById('bt-device-list');
      const sub = document.getElementById('bt-modal-sub');
      const connectBtn = document.getElementById('bt-connect-btn') as HTMLButtonElement;
      if (!list) return;
      if (sub) sub.textContent = `${sorted.length} device${sorted.length !== 1 ? 's' : ''} found nearby`;
      list.innerHTML = sorted.length === 0
        ? `<div id="bt-empty">No devices found yet...<br>Make sure Bluetooth is on and devices are nearby.</div>`
        : sorted.map((d, i) => {
            const name = d.name || 'Unknown device';
            const isSelected = selectedDevice === d;
            const isDefault = name === defaultName;
            const isPrinter = isPrinterDevice(name);
            return `<div class="bt-device-item${isSelected ? ' bt-selected' : ''}" data-idx="${i}">
              <div class="bt-device-icon">${getDeviceIcon(name)}</div>
              <div class="bt-device-info">
                <div class="bt-device-name">${name}</div>
                <div class="bt-device-type">${isPrinter ? 'Thermal printer · ESC/POS' : 'Bluetooth device'}</div>
              </div>
              <div class="bt-device-right">
                ${signalBars()}
                ${isDefault ? '<span class="bt-badge bt-badge-default">Default</span>' : isPrinter ? '<span class="bt-badge bt-badge-printer">Printer</span>' : ''}
              </div>
            </div>`;
          }).join('');

      list.querySelectorAll('.bt-device-item').forEach((el, i) => {
        (el as HTMLElement).addEventListener('click', () => {
          selectedDevice = sorted[i];
          renderList();
        });
      });

      if (connectBtn) {
        if (selectedDevice) {
          connectBtn.classList.add('active');
          connectBtn.textContent = `Connect & set default`;
        } else {
          connectBtn.classList.remove('active');
          connectBtn.textContent = 'Select a device';
        }
      }
    }

    overlay.innerHTML = `
      <div id="bt-modal">
        <div id="bt-modal-header">
          <div>
            <div id="bt-modal-title">🖨️ Bluetooth devices</div>
            <div id="bt-modal-sub">Scanning...</div>
          </div>
          <button id="bt-close-btn" style="width:28px;height:28px;border-radius:8px;border:1px solid #e2e8f0;background:#f8fafc;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;">✕</button>
        </div>
        <div id="bt-scan-bar">
          <div id="bt-scan-dot"></div>
          Scanning for nearby Bluetooth devices...
        </div>
        <div id="bt-device-list"></div>
        <div id="bt-modal-footer">
          <button id="bt-cancel-btn">Cancel</button>
          <button id="bt-connect-btn">Select a device</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    renderList();

    document.getElementById('bt-cancel-btn')?.addEventListener('click', () => { removeModal(); resolve(null); });
    document.getElementById('bt-close-btn')?.addEventListener('click', () => { removeModal(); resolve(null); });
    document.getElementById('bt-connect-btn')?.addEventListener('click', () => {
      if (selectedDevice) { removeModal(); resolve(selectedDevice); }
    });
    overlay.addEventListener('click', e => { if (e.target === overlay) { removeModal(); resolve(null); } });
  });
}

export async function connectBluetoothPrinter(): Promise<BluetoothRemoteGATTCharacteristic | null> {
  const bt = (navigator as any).bluetooth;
  if (!bt) {
    alert('Web Bluetooth is not supported in this browser. Use Chrome on Android/desktop.');
    return null;
  }

  showToast('🔍 Opening Bluetooth scanner...', '#1e293b');

  try {
    // Request a device — this opens browser's native picker but we wrap it with our UI
    // We use acceptAllDevices to show everything
    const device: BluetoothDevice = await bt.requestDevice({
      acceptAllDevices: true,
      optionalServices: [
        '000018f0-0000-1000-8000-00805f9b34fb',
        '00001101-0000-1000-8000-00805f9b34fb',
        '0000ff00-0000-1000-8000-00805f9b34fb',
        '0000ffe0-0000-1000-8000-00805f9b34fb',
        '00001800-0000-1000-8000-00805f9b34fb',
        '00001801-0000-1000-8000-00805f9b34fb',
      ],
    });

    removeToast();

    // Show our custom modal with just this one device (browser already did the picking)
    // But we enhance it — show confirmation + set default
    const confirmed = await showDevicePicker([device]);
    if (!confirmed) return null;

    localStorage.setItem(BT_PRINTER_NAME_KEY, confirmed.name || 'Printer');
    showToast(`🔗 Connecting to ${confirmed.name || 'device'}...`, '#1e293b');

    const server = await confirmed.gatt!.connect();
    const services = await server.getPrimaryServices();

    for (const service of services) {
      try {
        const chars = await service.getCharacteristics();
        for (const char of chars) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            removeToast();
            return char;
          }
        }
      } catch { continue; }
    }

    removeToast();
    alert('Connected but no writable characteristic found. This device may not support BLE printing.');
    return null;
  } catch (e: any) {
    removeToast();
    if (e?.name !== 'NotFoundError') console.error('BT connect failed:', e);
    return null;
  }
}

function taxIdLabelAr(label?: string): string {
  const k = (label || '').toUpperCase().replace(/[^A-Z]/g, '');
  if (k.includes('TRN')) return 'رقم التسجيل الضريبي';
  if (k.includes('GST')) return 'ضريبة السلع والخدمات';
  return 'الرقم الضريبي';
}

// Arabic lines for phone / email / tax ID, printed under the Arabic "To:" line.
// The values stay in Latin characters and digits so they remain exact.
function customerLinesAr(job: PrintJob): number[] {
  if (!job.clientLineAr && !job.rowLabelsAr) return [];
  const line = (labelAr: string, value: string) =>
    textLineBytes('\u2067' + labelAr + '\u2069: \u2066' + value + '\u2069', { fontSize: 20, align: 'left' });
  return [
    ...(job.clientPhone ? line('الهاتف', job.arabicNumerals ? toArabicIndicDigits(job.clientPhone) : job.clientPhone) : []),
    ...(job.clientEmail ? line('البريد الإلكتروني', job.clientEmail) : []),
    ...(job.buyerTaxId ? line(taxIdLabelAr(job.buyerTaxLabel), job.arabicNumerals ? toArabicIndicDigits(job.buyerTaxId) : job.buyerTaxId) : []),
  ];
}

// Shared by both Bluetooth and USB printing — builds the raw ESC/POS byte
// sequence for a receipt so both transports print identically formatted output.
const HEADER_AR: Record<string, string> = {
  item: 'الصنف', items: 'الأصناف', description: 'الوصف',
  qty: 'الكمية', quantity: 'الكمية',
  rate: 'السعر', price: 'السعر',
  amt: 'المبلغ', amount: 'المبلغ',
  tax: 'الضريبة', vat: 'الضريبة',
  discount: 'الخصم', disc: 'الخصم',
  unit: 'الوحدة', total: 'الإجمالي',
};
function colHeaderAr(c: PrintColumn): string {
  const key = c.header.trim().toLowerCase().replace(/[.%]/g, '');
  return HEADER_AR[key] || c.headerAr || '';
}

function rowInv(cells: string[], cols: PrintColumn[]): number[] {
  const p = primaryIndex(cols);
  return rasterizeNColLine(
    cells,
    colFractions(cols),
    cols.map((c, i) => c.align || (i === p ? 'left' : 'right')),
    { invert: true }
  );
}

export function buildReceiptBytes(job: PrintJob): number[] {
  const subtotal = job.netSubtotal ?? job.items.reduce(
    (s, i) => s + i.qty * i.rate,
    0
  );

  const discTotal = (job.discountLines || []).reduce(
    (s, d) => s + subtotal * d.rate / 100,
    0
  );

  // Discount reduces the taxable base before header-level tax is calculated —
  // same standard GST/VAT math as every other totals block in the app.
  // (productTaxTotal is unaffected here — it already arrives pre-computed
  // post-discount from buildQuickInvoicePrintJob / renderPOSReceiptToPdf.)
  const taxableValue = subtotal - discTotal;

  const taxTotal = (job.taxLines || []).reduce(
    (s, t) => s + taxableValue * t.rate / 100,
    0
  );

  const total =
    taxableValue +
    taxTotal +
    (job.shippingCost || 0) +
    (job.productTaxTotal || 0) -
    (job.autoDiscountAmount || 0);

  const sym = job.sym || '₹';

  const numAr = (n: number) => job.arabicNumerals ? toArabicIndicDigits(n.toFixed(2)) : n.toFixed(2);
  const pctAr = (r: number | string) => job.arabicNumerals ? toArabicIndicDigits(String(r)) : String(r);
  // Arabic row: label on the left, amount right-aligned (same layout as the item table)
  const arRow = (label: string | undefined, sign: string, amount: number, size = 20): number[] =>
    label ? rasterizeRow('\u2067' + label + '\u2069', sign + sym + numAr(amount), { fontSize: size }) : [];

  const output: number[] = [
    ...CMD.INIT,
    ...CMD.ALIGN_CENTER,

    // HEADER — from template text above the table, if we have it; else fallback
    ...(job.headerLines && job.headerLines.length > 0
      ? job.headerLines.flatMap((line, i) =>
          i === 0
            ? textLineBytes(line, { fontSize: 34, bold: true, align: 'center' }, job.arabicNumerals)
            : textLineBytes(line, { fontSize: 22, align: 'center' }, job.arabicNumerals)
        )
      : [
          ...CMD.BOLD_ON,
          ...CMD.DOUBLE_HEIGHT,
          ...textToBytes((job.shopName || 'RECEIPT') + '\n'),
          ...CMD.NORMAL_SIZE,
          ...CMD.BOLD_OFF,
          ...textToBytes((job.invoiceNumber || '') + '\n'),
          ...textToBytes((job.date || new Date().toLocaleDateString()) + '\n'),
        ]),

    ...CMD.ALIGN_LEFT,
    ...divider(),

    // CUSTOMER
    ...(job.clientName
      ? [
          ...textLineBytes('To: ' + job.clientName, { fontSize: 22, align: 'left' }),
          ...(job.clientPhone ? textLineBytes('Ph: ' + job.clientPhone, { fontSize: 20, align: 'left' }) : []),
          ...(job.clientEmail ? textLineBytes('Email: ' + job.clientEmail, { fontSize: 20, align: 'left' }) : []),
          ...(job.buyerTaxId ? textLineBytes((job.buyerTaxLabel || 'Tax ID') + ': ' + job.buyerTaxId, { fontSize: 20, align: 'left' }) : []),
          ...(job.clientLineAr ? textLineBytes(job.clientLineAr, { fontSize: 22, align: 'left' }, job.arabicNumerals) : []),
          ...customerLinesAr(job),
          ...divider(),
        ]
      : []),

    // TEMPLATE FIELDS (Patient, Age, Power R/L/PD, etc.)
    ...(job.dynamicFields && job.dynamicFields.length > 0
      ? [
          ...job.dynamicFields.flatMap(f => {
            const enLine = `${f.label}: ${f.value}`;
            const arLine = f.labelAr && f.valueAr ? `${f.labelAr}: ${f.valueAr}` : null;
            return [
              ...textLineBytes(enLine, { fontSize: 22, align: 'left' }),
              ...(arLine ? textLineBytes(arLine, { fontSize: 22, align: 'left' }, job.arabicNumerals) : []),
            ];
          }),
          ...divider(),
        ]
      : []),

    // ITEMS
    ...(() => {
      const cols: PrintColumn[] = job.tableColumns?.length && job.itemCells
        ? job.tableColumns
        : [
            { header: 'Item', primary: true, width: 36 },
            { header: 'Qty', width: 10 },
            { header: 'Rate', width: 26 },
            { header: 'Amt', width: 28 },
          ];
      const bilingual = !!(job.rowLabelsAr?.item || cols.some(c => c.headerAr));
      return [
        ...rowInv(cols.map(c => c.header), cols),
        ...(bilingual ? rowInv(cols.map(colHeaderAr), cols) : []),
      ];
    })(),
    ...job.items.flatMap((item, itemIdx) => {
      const hasRealTranslation =
        !!item.arabicName &&
        item.arabicName.trim().toLowerCase() !== item.description.trim().toLowerCase();
      return [
        ...(job.tableColumns?.length && job.itemCells?.[itemIdx]
          ? rowN(job.itemCells[itemIdx], job.tableColumns)
          : row4(
              item.description,
              String(item.qty),
              `${sym}${item.rate.toFixed(2)}`,
              `${sym}${(item.qty * item.rate).toFixed(2)}`
            )),
        ...(job.itemExtraLines?.[itemIdx]
          ? textLineBytes('  ' + job.itemExtraLines[itemIdx], { fontSize: 20, align: 'left' })
          : []),
        ...(job.itemExtraLinesAr?.[itemIdx]
          ? textLineBytes('  ' + job.itemExtraLinesAr[itemIdx], { fontSize: 20, align: 'left' }, job.arabicNumerals)
          : []),
        ...(() => {
          const _cols = job.tableColumns;
          const _cells = job.itemCells?.[itemIdx];
          if (_cols?.length && _cells) {
            if (!hasRealTranslation && !job.arabicNumerals) return [];
            const _pi = Math.max(0, _cols.findIndex(c => c.primary));
            const _ar = _cells.map((t, i) => {
              if (i === _pi) return hasRealTranslation
        ? (job.arabicNumerals ? toArabicIndicDigits(item.arabicName!) : item.arabicName!)
        : '';
              return job.arabicNumerals && /[0-9]/.test(t) && !/[A-Za-z]/.test(t)
                ? toArabicIndicDigits(t)
                : '';
            });
            return _ar.some(t => t) ? rowN(_ar, _cols) : [];
          }
          return [
            ...(hasRealTranslation
              ? textLineBytes(item.arabicName!, { fontSize: 22, align: 'left' }, job.arabicNumerals)
              : []),
            ...(job.arabicNumerals && hasRealTranslation
              ? textLineBytes(
                  `${toArabicIndicDigits(String(item.qty))}   ${sym}${toArabicIndicDigits((item.qty * item.rate).toFixed(2))}`,
                  { fontSize: 20, align: 'left' }
                )
              : []),
          ];
        })(),
      ];
    }),

    ...divider(),

    // SUBTOTAL
    ...row('Subtotal', `${sym}${subtotal.toFixed(2)}`),
    ...arRow(job.rowLabelsAr?.subtotal, '', subtotal),

    // TAX
    ...(job.taxLines || []).flatMap(t =>
      t.rate > 0
        ? [
            ...row(`${t.label}(${t.rate}%)`, `+${sym}${(taxableValue * t.rate / 100).toFixed(2)}`),
            ...arRow(t.labelAr ? `${t.labelAr} (${pctAr(t.rate)}%)` : undefined, '+', taxableValue * t.rate / 100),
          ]
        : []
    ),

    // DISCOUNT
    ...(job.discountLines || []).flatMap(d =>
      d.rate > 0
        ? [
            ...row(`${d.label}(${d.rate}%)`, `-${sym}${(subtotal * d.rate / 100).toFixed(2)}`),
            ...arRow(d.labelAr ? `${d.labelAr} (${pctAr(d.rate)}%)` : undefined, '-', subtotal * d.rate / 100),
          ]
        : []
    ),

    // NAMED TAX BREAKDOWN (CGST/SGST/IGST/QST/etc.) — printed instead of
    // the generic blended line whenever the invoice's tax scheme is
    // component-based and a breakdown was actually computed upstream.
    ...(job.taxBreakdown && job.taxBreakdown.length > 0
      ? job.taxBreakdown.flatMap(t => [
          ...row(`${t.name} (${t.rate}%)`, `+${sym}${t.amount.toFixed(2)}`),
          ...arRow(t.nameAr ? `${t.nameAr} (${pctAr(t.rate)}%)` : undefined, '+', t.amount),
        ])
      // PRODUCT-LEVEL TAX fallback — blended rate, used when no named
      // breakdown is available (flat-tax schemes, or older saved invoices).
      : job.productTaxTotal && job.productTaxTotal > 0
      ? [
          ...row(
            `Total Tax (${((job.productTaxTotal / subtotal) * 100).toFixed(subtotal ? 1 : 0)}%)`,
            `+${sym}${job.productTaxTotal.toFixed(2)}`
          ),
          ...arRow(
            job.rowLabelsAr?.tax
              ? `${job.rowLabelsAr.tax} (${pctAr(((job.productTaxTotal / subtotal) * 100).toFixed(subtotal ? 1 : 0))}%)`
              : undefined,
            '+',
            job.productTaxTotal
          ),
        ]
      : []),

    // AUTO DISCOUNT
    ...(job.autoDiscountAmount && job.autoDiscountAmount > 0
      ? [
          ...row(
            job.autoDiscountLabel || 'Discount',
            `-${sym}${job.autoDiscountAmount.toFixed(2)}`
          ),
        ]
      : []),

    // SHIPPING
    ...(job.shippingCost && job.shippingCost > 0
      ? [
          ...row('Shipping', `+${sym}${job.shippingCost.toFixed(2)}`),
          ...arRow(job.rowLabelsAr?.shipping, '+', job.shippingCost),
        ]
      : []),

    // TOTAL
    ...CMD.BOLD_ON,
    ...row('TOTAL', `${sym}${total.toFixed(2)}`),
    ...arRow(job.rowLabelsAr?.total, '', total, 22),
    ...CMD.BOLD_OFF,

    ...divider(),

    // AMOUNT IN WORDS
    ...(job.amountInWords
      ? [
          ...CMD.ALIGN_CENTER,
          ...textLineBytes(`In Words: ${job.amountInWords}`, { fontSize: 20, align: 'center' }),
          ...(job.amountInWordsAr
            ? textLineBytes(job.amountInWordsAr, { fontSize: 20, align: 'center' }, job.arabicNumerals)
            : []),
        ]
      : []),

    // FOOTER
    ...CMD.ALIGN_CENTER,

    ...(job.notes
      ? textLineBytes(job.notes, { fontSize: 22, align: 'center' }, job.arabicNumerals)
      : []),
    ...(job.notesAr
      ? textLineBytes(job.notesAr, { fontSize: 22, align: 'center' }, job.arabicNumerals)
      : []),

    ...(job.footerLines && job.footerLines.length > 0
      ? job.footerLines.flatMap(line => textLineBytes(line, { fontSize: 22, align: 'center' }, job.arabicNumerals))
      : textLineBytes(job.footer || 'Thank you!', { fontSize: 22, align: 'center' }, job.arabicNumerals)),
  ];

  // INVOICE QR
  if (job.invoiceQR) {
  output.push(
    ...textToBytes('\n'),
    ...textLineBytes('Scan to view invoice', { fontSize: 22, align: 'center' }),
    ...(job.invoiceQRLabel ? textLineBytes(job.invoiceQRLabel, { fontSize: 22, align: 'center' }, job.arabicNumerals) : []),
    ...qrImage(job.invoiceQR, 4),
    ...[ESC, 0x64, 0x01]
  );
}
  // TRACKING QR
  if (job.trackingQR) {
    output.push(
      ...textToBytes('\n'),
      ...textLineBytes('Scan to track order', { fontSize: 22, align: 'center' }),
      ...(job.trackingQRLabel ? textLineBytes(job.trackingQRLabel, { fontSize: 22, align: 'center' }, job.arabicNumerals) : []),
      ...qrImage(job.trackingQR, 4),
      ...[ESC, 0x64, 0x01]
    );
  }

  // FINISH
  output.push(
    ...CMD.FEED,
    ...CMD.CUT
  );

  return output;
}
// Shared by POSView and QuickInvoiceView — resolves the bilingual "To: <name>"
// client line fresh at print time (never from a possibly-stale cache).
// Walk-in customers get the label translated; named clients get the name
// transliterated (phonetic re-spelling), never machine-translated.
export async function resolveClientLineAr(
  clientName: string,
  lang: string,
  backend: string,
  headers: Record<string, string>
): Promise<string | undefined> {
  const isWalkIn = !clientName?.trim() || clientName.trim().toLowerCase() === 'walk-in customer';
  try {
    const [toLabelRes, nameRes] = await Promise.all([
      fetch(`${backend}/translate?text=${encodeURIComponent('To:')}&tl=${lang}`, { headers }).then(r => r.json()),
      isWalkIn
        ? fetch(`${backend}/translate?text=${encodeURIComponent('Walk-in Customer')}&tl=${lang}`, { headers }).then(r => r.json())
        : fetch(`${backend}/transliterate?text=${encodeURIComponent(clientName.trim())}&tl=${lang}`, { headers }).then(r => r.json()),
    ]);
    const toLabel = toLabelRes.translated || 'To:';
    const nameAr = isWalkIn ? (nameRes.translated || 'Walk-in Customer') : (nameRes.transliterated || clientName);
    if (!isWalkIn && nameAr.trim().toLowerCase() === clientName.trim().toLowerCase()) {
      return undefined; // no real transliteration happened — don't print a duplicate
    }
    return `${toLabel} ${nameAr}`;
  } catch {
    return undefined;
  }
}

export async function printReceiptBT(job: PrintJob): Promise<boolean> {
  showToast('🖨️ Connecting to Bluetooth printer...', '#1e293b');
  try {
    const characteristic = await connectBluetoothPrinter();
    if (!characteristic) { removeToast(); return false; }

    showToast('📄 Printing receipt...', '#1d4ed8');
    const bytes = buildReceiptBytes(job);

    const chunk = 20;
    for (let i = 0; i < bytes.length; i += chunk) {
      await characteristic.writeValue(new Uint8Array(bytes.slice(i, i + chunk)));
      await new Promise(r => setTimeout(r, 30));
    }

    showToast('✅ Printed successfully!', '#15803d', 2500);
    return true;
  } catch (e) {
    removeToast();
    console.error('BT print failed:', e);
    return false;
  }
}

export function getBTPrinterName(): string | null {
  return localStorage.getItem(BT_PRINTER_NAME_KEY);
}